import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Barcode, Boxes, Calendar, Factory, ScanLine, Plus, ArrowLeft, Search, Download,
  Loader2, X, FileText,
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const inputCls = "w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-slate-100 placeholder:text-slate-600 focus:border-[#C6A15B]/60 focus:outline-none";
const labelCls = "block text-xs font-medium text-slate-400 mb-1.5";
const btnGold = "inline-flex items-center justify-center gap-2 text-sm px-4 py-2.5 rounded-lg bg-[#C6A15B] text-black font-medium hover:bg-[#d4b36e] transition-colors disabled:opacity-60";
const btnGhost = "inline-flex items-center justify-center gap-2 text-sm px-4 py-2.5 rounded-lg bg-white/[0.05] border border-white/10 text-slate-200 hover:bg-white/[0.08] transition-colors";
const fmtDate = (iso) => { try { return new Date(iso + 'T00:00:00').toLocaleDateString(); } catch { return iso; } };
const fmtDateTime = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso; } };

export const ProductionBarcodes = ({ user, setActiveTab }) => {
  const [stats, setStats] = useState(null);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ search: '', production_date: '', batch: '', product: '', factory: '', po_number: '' });
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ production_date: '', batch_number: '', quantity: '', product_type: '', factory: '', po_number: '', production_reference: '', notes: '' });
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [batchDetail, setBatchDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [barcodeSearch, setBarcodeSearch] = useState('');
  const [lookupValue, setLookupValue] = useState('');
  const [lookupResult, setLookupResult] = useState(null);
  const [lookupError, setLookupError] = useState('');
  const token = localStorage.getItem('token');

  const loadStats = async () => { try { const r = await axios.get(`${API}/api/admin/barcodes/stats`); setStats(r.data); } catch (e) { /* noop */ } };
  const loadBatches = async (overrideFilters) => {
    const f = overrideFilters || filters; setLoading(true);
    try {
      const params = {}; Object.entries(f).forEach(([k, v]) => { if (v) params[k] = v; });
      const r = await axios.get(`${API}/api/admin/barcodes/batches`, { params }); setBatches(r.data.batches || []);
    } catch (e) { toast.error('Failed to load production batches'); } finally { setLoading(false); }
  };
  useEffect(() => { loadStats(); loadBatches(); /* eslint-disable-next-line */ }, []);

  const handleCreate = async () => {
    if (!form.production_date || !form.batch_number || !form.quantity) { toast.error('Production Date, Batch Number and Quantity are required'); return; }
    const batchNum = parseInt(form.batch_number, 10); const qty = parseInt(form.quantity, 10);
    if (isNaN(batchNum) || batchNum < 1 || batchNum > 99) { toast.error('Batch number must be between 1 and 99'); return; }
    if (isNaN(qty) || qty < 1 || qty > 99999) { toast.error('Quantity must be between 1 and 99,999'); return; }
    setCreating(true);
    try {
      const r = await axios.post(`${API}/api/admin/barcodes/batches`, {
        production_date: form.production_date, batch_number: batchNum, quantity: qty,
        product_type: form.product_type || null, factory: form.factory || null, po_number: form.po_number || null,
        production_reference: form.production_reference || null, notes: form.notes || null,
      });
      toast.success(r.data.message); setShowCreate(false);
      setForm({ production_date: '', batch_number: '', quantity: '', product_type: '', factory: '', po_number: '', production_reference: '', notes: '' });
      loadStats(); loadBatches();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create batch'); } finally { setCreating(false); }
  };

  const openBatch = async (batch, search = '') => {
    setSelectedBatch(batch); setDetailLoading(true);
    try { const r = await axios.get(`${API}/api/admin/barcodes/batches/${batch.batch_id}`, { params: { search: search || undefined, limit: 100 } }); setBatchDetail(r.data); }
    catch (e) { toast.error('Failed to load batch details'); } finally { setDetailLoading(false); }
  };
  const handleLookup = async () => {
    const val = lookupValue.trim(); if (!val) return; setLookupError(''); setLookupResult(null);
    try { const r = await axios.get(`${API}/api/admin/barcodes/lookup`, { params: { barcode_id: val } }); setLookupResult(r.data); }
    catch (e) { setLookupError(e.response?.data?.detail || 'Barcode not found'); }
  };
  const downloadExport = (batchId, kind) => {
    const url = `${API}/api/admin/barcodes/batches/${batchId}/export/${kind}?token=${encodeURIComponent(token)}`;
    const a = document.createElement('a'); a.href = url; a.rel = 'noopener'; a.style.display = 'none';
    document.body.appendChild(a); a.click(); setTimeout(() => document.body.removeChild(a), 1000);
  };

  const card = "bg-[#14171B] border border-white/[0.08] rounded-2xl";

  // ---------------- BATCH DETAIL ----------------
  if (selectedBatch) {
    const b = batchDetail?.batch || selectedBatch;
    const info = [['Product', b.product_type], ['Factory', b.factory], ['PO Number', b.po_number], ['Reference', b.production_reference], ['Created By', b.created_by], ['Created', fmtDateTime(b.created_at)]];
    return (
      <div className="space-y-6" data-testid="barcode-batch-detail">
        <button className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-white" onClick={() => { setSelectedBatch(null); setBatchDetail(null); setBarcodeSearch(''); }} data-testid="barcode-back-btn"><ArrowLeft className="w-4 h-4" /> Back to batches</button>
        <div className={`${card} p-6`}>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <h2 className="text-xl font-semibold text-white flex items-center gap-2"><Boxes className="w-5 h-5 text-[#C6A15B]" /> Batch {b.batch_code} <span className="text-slate-500">— {b.barcode_prefix}</span></h2>
            <div className="flex gap-2">
              <button className={btnGold} onClick={() => downloadExport(b.batch_id, 'factory')} data-testid="export-factory-btn"><Download className="w-4 h-4" /> Factory Export</button>
              <button className={btnGhost} onClick={() => downloadExport(b.batch_id, 'ids')} data-testid="export-ids-btn"><Download className="w-4 h-4" /> IDs (CSV)</button>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mt-5">
            {info.map(([k, v]) => <div key={k}><span className="text-slate-500 block text-xs">{k}</span><span className="text-slate-200">{v || '—'}</span></div>)}
            {b.notes && <div className="col-span-2"><span className="text-slate-500 block text-xs">Notes</span><span className="text-slate-200">{b.notes}</span></div>}
          </div>
        </div>

        <div className={`${card} p-6`}>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <h3 className="text-white font-semibold">Individual Barcodes {batchDetail?.pagination ? <span className="text-slate-500">({batchDetail.pagination.total})</span> : ''}</h3>
            <div className="flex gap-2">
              <div className="relative">
                <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
                <input className={`${inputCls} pl-9`} placeholder="Search a barcode in this batch..." value={barcodeSearch} onChange={(e) => setBarcodeSearch(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && openBatch(selectedBatch, barcodeSearch)} data-testid="in-batch-search-input" />
              </div>
              <button className={btnGold} onClick={() => openBatch(selectedBatch, barcodeSearch)} data-testid="in-batch-search-btn">Search</button>
            </div>
          </div>
          {detailLoading ? <p className="text-slate-500 text-sm">Loading…</p> : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {(batchDetail?.barcodes || []).map((bc) => (
                <div key={bc.barcode_id} className="border border-white/10 rounded-lg p-3 bg-black/20 flex flex-col items-center" data-testid={`barcode-item-${bc.barcode_id}`}>
                  <img src={bc.barcode_image} alt={bc.barcode_id} className="bg-white rounded p-1 max-w-full" style={{ height: 70 }} />
                  <span className={`text-xs mt-2 ${bc.status === 'VOID' ? 'line-through text-red-400' : 'text-slate-200'}`}>{bc.status}</span>
                </div>
              ))}
              {batchDetail?.pagination?.has_more && <p className="text-slate-500 text-sm col-span-full">Showing first {batchDetail.pagination.limit}. Use search to find a specific barcode.</p>}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ---------------- MAIN ----------------
  const previewId = (() => { try { return `TC${new Date(form.production_date + 'T00:00:00').toISOString().slice(2, 10).replace(/-/g, '')}${String(parseInt(form.batch_number || '0', 10)).padStart(2, '0')}00001`; } catch { return 'TCYYMMDD0000001'; } })();
  const STAT = [
    { t: 'Total Barcodes', v: stats?.total_barcodes ?? '—', icon: Barcode, gold: true, id: 'card-total-barcodes' },
    { t: 'Production Batches', v: stats?.total_batches ?? '—', icon: Boxes, id: 'card-total-batches' },
    { t: 'Generated This Month', v: stats?.barcodes_this_month ?? '—', icon: Calendar, id: 'card-month-barcodes' },
    { t: 'Latest Batch', v: stats?.latest_batch ? stats.latest_batch.barcode_prefix : '—', sub: stats?.latest_batch ? fmtDate(stats.latest_batch.production_date) : null, icon: Factory, id: 'card-latest-batch', small: true },
  ];
  return (
    <div className="space-y-6" data-testid="production-barcodes-section">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold text-white flex items-center gap-2"><Barcode className="w-6 h-6 text-[#C6A15B]" /> Production Barcodes</h2>
          <p className="text-slate-400 text-sm mt-1">Generate and trace factory CODE 128 barcode batches.</p>
        </div>
        <div className="flex gap-2">
          {setActiveTab && <button className={btnGhost} onClick={() => setActiveTab('serial-search')} data-testid="goto-serial-search"><ScanLine className="w-4 h-4" /> Serial Search</button>}
          <button className={btnGold} onClick={() => setShowCreate(true)} data-testid="create-batch-btn"><Plus className="w-4 h-4" /> Create Batch</button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {STAT.map((s) => (
          <div key={s.id} className={`${card} p-5`} data-testid={s.id}>
            <div className="flex items-center gap-2 text-slate-500 text-xs uppercase tracking-wide"><s.icon className="w-4 h-4" /> {s.t}</div>
            <div className={`${s.small ? 'text-lg' : 'text-3xl'} font-bold mt-1 ${s.gold ? 'text-[#C6A15B]' : 'text-white'}`}>{s.v}</div>
            {s.sub && <div className="text-xs text-slate-500">{s.sub}</div>}
          </div>
        ))}
      </div>

      {/* Lookup */}
      <div className={`${card} p-6`}>
        <h3 className="text-white font-semibold flex items-center gap-2 mb-3"><ScanLine className="w-5 h-5 text-[#C6A15B]" /> Barcode Lookup</h3>
        <div className="flex gap-2">
          <input className={inputCls} placeholder="Type or scan a barcode ID (e.g. TC2608280300125)" value={lookupValue} onChange={(e) => setLookupValue(e.target.value.toUpperCase())} onKeyDown={(e) => e.key === 'Enter' && handleLookup()} data-testid="barcode-lookup-input" />
          <button className={btnGold} onClick={handleLookup} data-testid="barcode-lookup-btn">Lookup</button>
        </div>
        {lookupError && <p className="text-red-400 text-sm mt-3" data-testid="lookup-error">{lookupError}</p>}
        {lookupResult && (
          <div className="mt-4 border border-white/10 rounded-lg p-4 bg-black/20" data-testid="lookup-result">
            <div className="flex flex-col sm:flex-row gap-4 items-center">
              <img src={lookupResult.barcode_image} alt={lookupResult.barcode_id} className="bg-white rounded p-2" style={{ height: 90 }} />
              <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm flex-1">
                {[['Barcode ID', lookupResult.barcode_id, true], ['Status', lookupResult.status], ['Production Date', fmtDate(lookupResult.production_date)], ['Batch', lookupResult.batch_code], ['Roll', lookupResult.serial_code], ['Product', lookupResult.product_type || '—'], ['Factory', lookupResult.factory || '—'], ['PO', lookupResult.po_number || '—'], ['Created By', lookupResult.created_by], ['Created', fmtDateTime(lookupResult.created_at)]].map(([k, v, gold]) => (
                  <div key={k}><span className="text-slate-500 text-xs block">{k}</span><span className={gold ? 'text-[#C6A15B] font-mono' : 'text-slate-200'}>{v}</span></div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Batch history */}
      <div className={`${card} p-6`}>
        <h3 className="text-white font-semibold mb-3">Production Batch History</h3>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
          <input className={inputCls} placeholder="Search..." value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })} data-testid="filter-search" />
          <input className={inputCls} type="date" value={filters.production_date} onChange={(e) => setFilters({ ...filters, production_date: e.target.value })} data-testid="filter-date" />
          <input className={inputCls} placeholder="Batch #" value={filters.batch} onChange={(e) => setFilters({ ...filters, batch: e.target.value })} data-testid="filter-batch" />
          <input className={inputCls} placeholder="Product" value={filters.product} onChange={(e) => setFilters({ ...filters, product: e.target.value })} data-testid="filter-product" />
          <input className={inputCls} placeholder="Factory" value={filters.factory} onChange={(e) => setFilters({ ...filters, factory: e.target.value })} data-testid="filter-factory" />
          <input className={inputCls} placeholder="PO Number" value={filters.po_number} onChange={(e) => setFilters({ ...filters, po_number: e.target.value })} data-testid="filter-po" />
        </div>
        <div className="flex gap-2 mt-3">
          <button className={btnGold} onClick={() => loadBatches()} data-testid="apply-filters-btn">Apply</button>
          <button className={btnGhost} onClick={() => { const cleared = { search: '', production_date: '', batch: '', product: '', factory: '', po_number: '' }; setFilters(cleared); loadBatches(cleared); }}>Clear</button>
        </div>
        <div className="mt-4">
          {loading ? <p className="text-slate-500 text-sm py-6">Loading…</p> : batches.length === 0 ? (
            <p className="text-slate-500 text-center py-10" data-testid="no-batches">No production batches yet. Create your first batch to generate barcodes.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[820px]" data-testid="batch-history-table">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wide text-slate-500 border-b border-white/[0.06]">
                    {['Production Date', 'Batch', 'Product', 'Factory', 'PO', 'Qty', 'Created By', 'Created', 'Status', ''].map((h, i) => <th key={i} className="px-3 py-2.5 font-medium">{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {batches.map((b) => (
                    <tr key={b.batch_id} className="border-b border-white/[0.04] last:border-0 hover:bg-white/[0.03]" data-testid={`batch-row-${b.batch_code}`}>
                      <td className="px-3 py-2.5 text-slate-200">{fmtDate(b.production_date)}</td>
                      <td className="px-3 py-2.5 text-[#C6A15B] font-mono">{b.batch_code}</td>
                      <td className="px-3 py-2.5 text-slate-300">{b.product_type || '—'}</td>
                      <td className="px-3 py-2.5 text-slate-300">{b.factory || '—'}</td>
                      <td className="px-3 py-2.5 text-slate-300">{b.po_number || '—'}</td>
                      <td className="px-3 py-2.5 text-slate-200">{b.quantity}</td>
                      <td className="px-3 py-2.5 text-slate-400">{b.created_by}</td>
                      <td className="px-3 py-2.5 text-slate-400">{fmtDateTime(b.created_at)}</td>
                      <td className="px-3 py-2.5"><span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300">{b.status}</span></td>
                      <td className="px-3 py-2.5"><button className="text-[#C6A15B] text-sm hover:underline" onClick={() => openBatch(b)} data-testid={`view-batch-${b.batch_code}`}>View</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Create dialog */}
      {showCreate && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="create-batch-dialog">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setShowCreate(false)} />
          <div className={`relative w-full max-w-lg ${card} max-h-[92vh] overflow-y-auto`}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/[0.08]">
              <h3 className="text-white font-semibold flex items-center gap-2"><Boxes className="w-5 h-5 text-[#C6A15B]" /> Create Production Batch</h3>
              <button onClick={() => setShowCreate(false)} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div><label className={labelCls}>Production Date *</label><input className={inputCls} type="date" value={form.production_date} onChange={(e) => setForm({ ...form, production_date: e.target.value })} data-testid="form-production-date" /></div>
                <div><label className={labelCls}>Batch # *</label><input className={inputCls} type="number" min="1" max="99" placeholder="03" value={form.batch_number} onChange={(e) => setForm({ ...form, batch_number: e.target.value })} data-testid="form-batch-number" /></div>
                <div><label className={labelCls}>Quantity *</label><input className={inputCls} type="number" min="1" max="99999" placeholder="1000" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} data-testid="form-quantity" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className={labelCls}>Product / Film Type</label><input className={inputCls} value={form.product_type} onChange={(e) => setForm({ ...form, product_type: e.target.value })} data-testid="form-product" /></div>
                <div><label className={labelCls}>Factory</label><input className={inputCls} value={form.factory} onChange={(e) => setForm({ ...form, factory: e.target.value })} data-testid="form-factory" /></div>
                <div><label className={labelCls}>PO Number</label><input className={inputCls} value={form.po_number} onChange={(e) => setForm({ ...form, po_number: e.target.value })} data-testid="form-po" /></div>
                <div><label className={labelCls}>Production Reference</label><input className={inputCls} value={form.production_reference} onChange={(e) => setForm({ ...form, production_reference: e.target.value })} data-testid="form-reference" /></div>
              </div>
              <div><label className={labelCls}>Notes</label><input className={inputCls} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="form-notes" /></div>
              {form.production_date && form.batch_number && form.quantity && (
                <div className="border border-[#C6A15B]/40 rounded-lg p-3 bg-black/20 text-sm" data-testid="batch-preview">
                  <span className="text-slate-500 text-xs block mb-1">Preview — will generate {form.quantity} barcodes:</span>
                  <span className="text-[#C6A15B] font-mono">{previewId} … {String(parseInt(form.quantity || '0', 10)).padStart(5, '0')}</span>
                </div>
              )}
              <div className="flex gap-3 pt-1">
                <button className={`${btnGhost} flex-1`} onClick={() => setShowCreate(false)}>Cancel</button>
                <button className={`${btnGold} flex-1`} onClick={handleCreate} disabled={creating} data-testid="confirm-create-batch-btn">{creating ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating…</> : <><FileText className="w-4 h-4" /> Generate Barcodes</>}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductionBarcodes;
